import "./CsApp.scss";
import { FC, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Navbar from "./components/CustomNavbar/Navbar";
import AppRoutes from "./routes/AppRoutes";
import { Grid } from "@mui/material";
import ErrorBoundary from "./ErrorBoundary";
import { useAppDispatch, useAppSelector } from "./redux-store/CustomReduxHooks";
import {
  updateTraceId,
  setDeviceType,
  setUserAgentDetails,
  setIpAddress,
} from "./redux-store/reducers/CsAppSlice";
import {
  useLazyFetchMasterDataQuery,
  useLazyUnifiedOfferDetailQuery,
} from "./features/Feature_GeneralAppElements/GeneralAppElements_Api";
import {
  setIsOfferMartApiRunning,
  setMasterData,
  setUnifiedOfferDetails,
} from "./features/Feature_GeneralAppElements/GeneralAppElements_Slice";
import ErrorComp from "./components/CustomError/ErrorComp";
import useDeviceDetection from "./hooks/useDeviceDetection";
import Loading from "./pages/Utilities/Loading";
import IdleTimeOut from "./pages/IdleTimeOut/IdleTimeOut";
import GenerateSsoOld from "./features/Feature_SSO/OldSSO/GenerateSsoOld";

import {
  useLazyFetchErrorVariableQuery,
  useLazyFetchHlSuccessMessagesQuery,
  useLazyFetchIntegrationVariableQuery,
  useLazyFetchNoHoldingNewProductCardsQuery,
  useLazyFetchNudgeDataQuery,
  useLazyFetchPageMessageQuery,
  useLazyFetchPageVariableQuery,
  useLazyFetchPromotionalCardQuery,
  useLazyFetchUnifiedOfferMartsQuery,
} from "./features/Features_Cms/Cms_Api";
import {
  setCmsPageOfferMartData,
  setErrorVariables,
  setHlSuccessMessages,
  setIntegrationVariables,
  setNoHoldingNewProductCards,
  setNudgeData,
  setPageMessage,
  setPagePromotionalCardData,
  setPageVariables,
} from "./features/Features_Cms/Feature_Cms_Slice";
import { nanoid } from "@reduxjs/toolkit";
import {
  getBrowserInfo,
  getCMSLink,
  getMobileOperatingSystem,
  getModuleNameFromPath,
} from "./utils/helper";
import { NativeBridge } from "./features/Feature_Native/NativeBridgeComp";
import CleverTapComp from "./features/Feature_CleverTap/CleverTapComp";
import GtmBaseLogic from "./features/Feature_Gtm/Gtm_BaseLogic";
import axios, { AxiosResponse } from "axios";
import { API_STATUS_CODE, ipifyClientsIp } from "./utils/ApiEndpoints";
import CsSessionStorageUtils from "./utils/CsSessionStorageUtils";
import { isEmpty } from "lodash";
import CustomAppDownTimePopUp from "./components/CustomAppDownTimePopUp/CustomAppDownTimePopUp";
import {
  R_ABCDRoutes,
  R_GeneralElementsRoutes,
  R_HiRoutes,
  R_HLRoutes,
  R_HomeRoutes,
  R_LiRoutes,
  R_MfRoutes,
  R_SSRoutes,
} from "./utils/Routes";
import { filterValidOfferMartData } from "./utils/conversionUtils";
import { T_CmsPageVariables } from "./features/Features_Cms/CmsPageVariable.Types";
import {
  enableServiceAppDowntimeFlag,
  iOSType,
  platformWeb,
} from "./utils/constants";
import pageVariablesDefault from "./assets/testData/CMS_Defaults/pageVariables.json";
import useQuickActionMenu from "./hooks/useQuickActionMenu";
import CustomServiceDownTimePopUp from "./components/CustomServiceDownTimePopUp/CustomServiceDownTimePopUp";
import pagePromotionalCardDefault from "./assets/testData/CMS_Defaults/promotionalCard.json";
import { T_CmsPagePromotionalCardData } from "./features/Features_Cms/CmsPromotinalCard.Types";
import { T_CmsNudgeData } from "./features/Features_Cms/CmsSuccessNudge.Types";
import { useSaveUIException } from "./hooks/useSaveUIException";

const pageVariablesDefaultMap = pageVariablesDefault.map((r) => {
  return {
    ...r,
    attributes: { ...r.attributes },
  };
});

const pagePromotionalCardDefaultMap = pagePromotionalCardDefault.map((r) => {
  return {
    ...r,
    attributes: { ...r.attributes },
  };
});

const CsApp: FC = () => {
  const appDispatch = useAppDispatch();
  const userDetails = useAppSelector((state) => state.CsAppSlice.userDetails);
  const { pathname } = useLocation();
  const [fetchMasterDataApi] = useLazyFetchMasterDataQuery();
  const { triggerSaveUIException } = useSaveUIException();
  const isPwaMobileOrWeb = useDeviceDetection();
  const GeneralAppElementsSlice = useAppSelector(
    (state) => state.GeneralAppElementsSlice
  );
  const [fetchPageVariable] = useLazyFetchPageVariableQuery();
  const [fetchCmsIntegration] = useLazyFetchIntegrationVariableQuery();
  const [fetchErrorVariable] = useLazyFetchErrorVariableQuery();
  const [fetchPageMessage] = useLazyFetchPageMessageQuery();
  const [fetchHlSuccessMessages] = useLazyFetchHlSuccessMessagesQuery();
  const [fetchCMSOfferMart] = useLazyFetchUnifiedOfferMartsQuery();
  const [fetchUnifiedOfferDetails] = useLazyUnifiedOfferDetailQuery();
  const [fetchNoHoldingNewProductCards] =
    useLazyFetchNoHoldingNewProductCardsQuery();
  useQuickActionMenu();

  const [fetchPromotionalCard] = useLazyFetchPromotionalCardQuery();
  const [fetchNudgeData] = useLazyFetchNudgeDataQuery();
  /** Trace id created for each session */
  if (isEmpty(CsSessionStorageUtils.get("TRACE_ID"))) {
    let randomTraceId = "CS" + nanoid(14);
    appDispatch(updateTraceId(randomTraceId));
    CsSessionStorageUtils.set("TRACE_ID", randomTraceId);
  }
  const csAppSlice = useAppSelector((state) => state.CsAppSlice);
  const masterData = GeneralAppElementsSlice.masterData;
  const cmsSlice = useAppSelector((state) => state.CmsSlice);
  const os = csAppSlice.NativeDeviceInfo?.deviceName
    ? getMobileOperatingSystem() === iOSType
      ? "ios"
      : "android"
    : platformWeb;

  useEffect(() => {
    if (!isEmpty(CsSessionStorageUtils.get("TRACE_ID"))) {
      const randomTraceId = CsSessionStorageUtils.get("TRACE_ID");
      appDispatch(updateTraceId(randomTraceId));
    }
    /** save clients IP address start */
    axios
      .get(ipifyClientsIp)
      .then((r: AxiosResponse) => r?.data?.ip)
      .then((ip) => {
        appDispatch(setIpAddress(ip || ""));
      })
      .catch((error) => {
        const currentPath = window?.location?.pathname;
        triggerSaveUIException({
          moduleName: getModuleNameFromPath(currentPath),
          pageName: currentPath || "",
          statusCode: 500,
          uiDisplayMessage: `fileName(CsApp.tsx) and functionName(get(ipifyClientsIp)) `,
          errorMessage: {
            stk: error.stack || "No stack available",
            msg: error.message || "Unknown error occurred",
          },
        });
      });
    /** save clients IP address end */
    const browserInfo = getBrowserInfo();
    appDispatch(
      setUserAgentDetails({
        platform: "web",
        deviceId: "",
        deviceType: "web",
        browserName: browserInfo.browserName,
        browserVersion: browserInfo.fullVersion,
      })
    );
  }, []);

  useEffect(() => {
    if (isPwaMobileOrWeb && isPwaMobileOrWeb.length > 0) {
      CsSessionStorageUtils.set("DEVICE_TYPE", isPwaMobileOrWeb);
      appDispatch(setDeviceType(isPwaMobileOrWeb));
    }
  }, [isPwaMobileOrWeb]);

  const fetchOfferDetails = async () => {
    if (!userDetails?.mobileNumber) return;
    try {
      appDispatch(setIsOfferMartApiRunning(true));
      const [offerDetailsResponse, cmsOfferResponse] = await Promise.all([
        fetchUnifiedOfferDetails({
          mobile_number: userDetails.mobileNumber,
        }),
        fetchCMSOfferMart(),
      ]);
      appDispatch(setIsOfferMartApiRunning(false));
      const offerMartDetail: any = offerDetailsResponse?.data?.payload?.[0];
      const offerMartCardData: any = cmsOfferResponse?.data?.payload.data;
      const finalOfferMartCard = filterValidOfferMartData(
        { ...offerMartDetail },
        offerMartCardData,
        csAppSlice
      );
      appDispatch(
        setUnifiedOfferDetails(offerDetailsResponse?.data?.payload?.[0] || {})
      );
      appDispatch(setCmsPageOfferMartData(finalOfferMartCard));
    } catch (error) {
      appDispatch(setIsOfferMartApiRunning(false));
      console.error("Error fetching offer details:", error);
    }
  };

  useEffect(() => {
    if (isEmpty(cmsSlice.CmsPageVariableData)) {
      fetchPageVariable().then((res) => {
        let updatedData: T_CmsPageVariables[] = [];
        if (res.isSuccess && res.data) {
          updatedData = res.data.payload.data.map((item) => ({
            ...item,
            attributes: {
              ...item.attributes,
              originalUrl: item.attributes.url || "",
              url: getCMSLink(item.attributes.url || "", os),
            },
          }));
        } else {
          updatedData = pageVariablesDefaultMap as T_CmsPageVariables[];
        }
        appDispatch(setPageVariables(updatedData));
      });
    }
  }, []);

  useEffect(() => {
    if (!isEmpty(userDetails?.customerAndLobDetails)) {
      fetchOfferDetails();
    }
  }, [userDetails?.mobileNumber, userDetails?.customerAndLobDetails]);

  useEffect(() => {
    if (!masterData?.masterDetail && userDetails?.customerAndLobDetails) {
      fetchMasterDataApi("all").then((res) => {
        appDispatch(setMasterData(res.data?.payload));
      });

      fetchCmsIntegration().then((res) => {
        if (res.isSuccess && res.data) {
          const updatedData = res.data.payload.data.map((item) => ({
            ...item,
            attributes: {
              ...item.attributes,
              originalUrl: item.attributes.url || "",
              url: getCMSLink(item.attributes.url || "", os),
            },
          }));
          appDispatch(setIntegrationVariables(updatedData));
        }
      });
      if (isEmpty(cmsSlice.CmsPagePromotionalCardData)) {
        fetchPromotionalCard().then((res) => {
          let updatedData: T_CmsPagePromotionalCardData[] = [];
          if (res.isSuccess && res.data) {
            updatedData = res.data.payload.data.map((item) => ({
              ...item,
              attributes: {
                ...item.attributes,
                originalUrl: item.attributes.url || "",
                url: getCMSLink(item.attributes.url || "", os),
              },
            }));
          } else {
            updatedData =
              pagePromotionalCardDefaultMap as T_CmsPagePromotionalCardData[];
          }
          appDispatch(setPagePromotionalCardData(updatedData));
        });
      }
      fetchErrorVariable().then((res) => {
        if (res.isSuccess && res.data) {
          appDispatch(setErrorVariables(res.data?.payload.data));
        }
      });
      fetchPageMessage().then((res) => {
        if (res.isSuccess && res.data) {
          const updatedData = res.data.payload.data.map((item) => ({
            ...item,
            attributes: {
              ...item.attributes,
              originalUrl: item.attributes.nudgeLink,
              nudgeLink: getCMSLink(item.attributes.nudgeLink || "", os),
            },
          }));
          appDispatch(setPageMessage(updatedData));
        }
      });
      fetchNoHoldingNewProductCards().then((res) => {
        if (res.isSuccess && res.data) {
          let updatedData = res.data.payload.data.map((item) => ({
            ...item,
            attributes: {
              ...item.attributes,
              originalUrl: item.attributes.redirectionURL || "",
              redirectionURL: getCMSLink(
                item.attributes.redirectionURL || "",
                os
              ),
            },
          }));
          appDispatch(setNoHoldingNewProductCards(updatedData));
        }
      });
      fetchNudgeData().then((res) => {
        if (
          res.data?.code === API_STATUS_CODE.SUCCESS &&
          !isEmpty(res.data.payload)
        ) {
          let updatedData: T_CmsNudgeData[] = [];
          updatedData = res.data.payload.data.map((item) => ({
            ...item,
            attributes: {
              ...item.attributes,
              originalUrl: item.attributes.redirectionURL || "",
              url: getCMSLink(item.attributes.redirectionURL || "", os),
            },
          }));
          appDispatch(setNudgeData(updatedData));
        }
      });
    }
  }, [userDetails?.customerAndLobDetails]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [pathname]);

  let isNavbarShow = [
    R_GeneralElementsRoutes.search,
    R_HomeRoutes.noHoldings,
    R_HLRoutes.home_loan_details,
    R_HLRoutes.interest_certificate,
    R_HLRoutes.emi_payment,
    R_HLRoutes.disbursement_request,
    R_HLRoutes.terms_and_conditions,
    R_HLRoutes.account_statement,
    R_HLRoutes.change_enach,
    R_HLRoutes.excess_emi_refund,
    R_HLRoutes.change_address,
    R_HLRoutes.document_upload,
    R_HLRoutes.home_loan_service_request,
    R_LiRoutes.details_PolicyNumber,
    R_LiRoutes.details_PolicyNumber_TaxCertificate,
    R_LiRoutes.detail_PolicyNumber_AddUpdateBankAccount,
    R_LiRoutes.details_PolicyNumber_PolicyAccountStatement,
    R_LiRoutes.details_PolicyNumber_PremiumReceipts,
    R_LiRoutes.details_PolicyNumber_UpdatePAN,
    R_LiRoutes.details_set_alerts,
    R_LiRoutes.detail_PolicyNumber_AddUpdateBankAccount,
    R_MfRoutes.mutualFund_Details,
    R_MfRoutes.mutualFund_CapitalGains,
    R_MfRoutes.mutualFund_AccountStatement,
    R_MfRoutes.mutualFund_ExitLoad,
    R_SSRoutes.details_Portfolio,
    R_SSRoutes.details_Portfolio_MyDocuments,
    R_SSRoutes.contact_notes,
    R_SSRoutes.account_statement,
    R_HiRoutes.details_PolicyNumber,
    R_HiRoutes.details_PolicyNumber_MedicalReports,
    R_HiRoutes.details_PolicyNumber_EditPolicy,
    R_LiRoutes.addUpdate_nominee,
    R_HLRoutes.home_loan_service_request,
    R_HLRoutes.home_loan_change_emi_tenure,
    R_HLRoutes.home_loan_change_emi_tenure_charges,
    R_HLRoutes.home_loan_change_emi_tenure_option3,
    R_HLRoutes.home_loan_change_emi_date,
    R_GeneralElementsRoutes.appTracker,
    R_HLRoutes.home_loan_tds_request,
    R_HLRoutes.home_loan_change_email,
    R_HLRoutes.home_loan_change_mobile,
    R_HLRoutes.Balance_Confirmation_Letter,
    R_HLRoutes.change_emi_select_date,
    R_HLRoutes.change_emi_upload_doc,
    R_GeneralElementsRoutes.taxCenter,
    R_ABCDRoutes.viewAll_transaction,
    ``,
  ].includes(pathname);

  let isHomeRoute = ["/home", "/login", "/onboarding"].includes(pathname);

  const getNavbar = () => {
    if (pathname) {
      if (isNavbarShow) {
        return <Navbar />;
      } else {
        return null;
      }
    } else {
      return null;
    }
  };
  return (
    <>
      <ErrorBoundary>
        {getNavbar()}
        <NativeBridge />
        <Grid
          container
          spacing={3}
          sx={{ display: "flex", justifyContent: "center" }}
        >
          <Grid item xs={12} sm={12} md={isHomeRoute ? 12 : 6}>
            <AppRoutes />
          </Grid>
        </Grid>
        <ErrorComp />
        <CustomServiceDownTimePopUp />
        <GenerateSsoOld key={"generateSSOOLD_Comp"} />
        <Loading />
        <IdleTimeOut />
        <CleverTapComp />
        <GtmBaseLogic />
        <CustomAppDownTimePopUp
          onClick={() => {}}
          open={enableServiceAppDowntimeFlag}
        />
      </ErrorBoundary>
    </>
  );
};

export default CsApp;
